import { useState } from 'react';
import { ErreurApi } from '../api/client';
import { useAnnulerAvis, useDemandeAvis } from '../api/hooks';
import { formatCourt } from '../format';
import { ConfirmerAvisImmediat } from './ConfirmerAvisImmediat';
import { SaisirTelephoneAvis } from './SaisirTelephoneAvis';
import { useToasts } from './Toasts';
import type { CommandeListee } from '../api/types';

/**
 * La demande d'avis : une case, pas un bouton.
 *
 * Un bouton dit « fais-le maintenant » ; ici rien ne part maintenant. Cocher
 * PROGRAMME la sollicitation pour la livraison + trois jours, recalée sur un
 * jour ouvré, et un passage de 10 h appelle le scénario ce jour-là. Une case
 * dit exactement cela : un état qu'on pose, qu'on relit et qu'on peut défaire.
 *
 * Elle se décoche donc tant que rien n'est parti. Une fois l'appel passé, la
 * case se fige : le client a reçu la sollicitation, et prétendre l'annuler
 * serait mentir sur ce qui s'est produit.
 */
/** Demande programmée pour un jour déjà passé : elle ne s'annule plus. */
export function avisEchu(commande: CommandeListee): boolean {
  const { avis } = commande;
  const echeance = avis.demande && avis.programmePour ? avis.programmePour.slice(0, 10) : null;
  return Boolean(echeance && echeance < new Date().toISOString().slice(0, 10));
}

export function CaseAvis({
  commande, enAttente, compact = false,
}: {
  commande: CommandeListee;
  enAttente?: boolean;
  /** Dans la liste : la date passe sous la case, sans phrase autour. */
  compact?: boolean;
}) {
  const programmer = useDemandeAvis();
  const annuler = useAnnulerAvis();
  const { ajouter } = useToasts();
  const { avis } = commande;

  const [confirmation, setConfirmation] = useState(false);
  const [saisieTelephone, setSaisieTelephone] = useState(false);

  /*
   * Pas de numéro composable, pas de demande : la fenêtre s'ouvre.
   *
   * Elle ne s'ouvrait que sur un numéro présent et illisible, un numéro absent
   * laissant passer la demande — le scénario devait se rabattre sur le mail.
   * Il ne le fait pas : la commande 39384 est partie avec `telephone: null` et
   * n'a joint personne. Les deux cas se valent donc, et la fenêtre les traite
   * ensemble ; elle montre le mauvais numéro quand il y en a un.
   */
  const aFournir = !avis.telephone.valide;

  const occupe = Boolean(enAttente) || programmer.isPending || annuler.isPending;

  /*
   * Deux états que seule l'Archive montre, faute d'y voir d'autres commandes.
   *
   * « Avis Google » coché à la main : la demande a été faite hors de l'outil.
   * La case le dit cochée — vide, elle offrirait de solliciter le client une
   * seconde fois.
   *
   * La fenêtre refermée : plus rien à cocher, et la case le montre plutôt que
   * d'attendre le refus de l'API au clic.
   */
  const horsOutil = !avis.demande && commande.etapes.avis.manuel?.valeur === true;
  const fenetrePassee = !avis.demande && !horsOutil && !avis.cochable;

  // Partie, ou échue : la case reste cochée et devient inerte. C'est un fait,
  // plus un choix — le passage de 10 h a dû l'envoyer.
  const fige = avis.envoye || avisEchu(commande) || horsOutil || fenetrePassee;

  async function cocher(telephone?: string): Promise<void> {
    try {
      const maj = await programmer.mutateAsync({ id: commande.id, telephone });
      setConfirmation(false);
      setSaisieTelephone(false);
      ajouter(
        maj.avis.envoye
          ? `Demande d'avis envoyée à ${commande.client}.`
          : `Avis programmé pour le ${formatCourt(maj.avis.programmePour)} — ${commande.client}.`,
        // Programmée mais non partie alors qu'elle devait l'être : le scénario
        // n'a pas répondu, et le dire en vert serait mentir.
        maj.avis.erreur ? 'bad' : 'ok',
      );
    } catch (err) {
      signaler(err);
    }
  }

  async function basculer(): Promise<void> {
    if (avis.demande) {
      try {
        await annuler.mutateAsync({ id: commande.id });
        ajouter(`Demande d'avis annulée pour ${commande.client}.`);
      } catch (err) { signaler(err); }
      return;
    }

    /*
     * Le numéro d'abord, l'avertissement ensuite.
     *
     * Une commande peut appeler les deux fenêtres : numéro illisible ET
     * échéance passée. La saisie prime, parce qu'elle conditionne l'envoi —
     * son bouton porte alors « Envoyer maintenant » plutôt que « Programmer »,
     * et dit donc la même chose que l'avertissement, au même moment. Deux
     * fenêtres à la suite pour un seul clic seraient une de trop.
     */
    if (aFournir) { setSaisieTelephone(true); return; }

    /*
     * Livrée depuis plus longtemps que le délai : l'échéance est déjà passée,
     * et cocher ne programme plus rien — cela envoie. On le dit avant, parce
     * que c'est le même geste pour deux conséquences opposées, et que
     * celle-ci ne se défait pas.
     */
    if (avis.partiraMaintenant) { setConfirmation(true); return; }
    await cocher();
  }

  function signaler(err: unknown): void {
    ajouter(
      err instanceof ErreurApi ? err.message : "La demande d'avis n'a pas pu être modifiée.",
      'bad',
    );
  }

  const etiquette = avis.envoye
    ? `Avis demandé le ${formatCourt(avis.envoyeLe ?? avis.programmePour)}`
    : avis.demande
      ? `Avis programmé le ${formatCourt(avis.programmePour)}`
      : horsOutil
        ? "Avis demandé hors de l'outil"
        : 'Demander un avis';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <label
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: compact ? 12.5 : 13,
          cursor: fige || occupe ? 'default' : 'pointer',
          opacity: fige ? 0.75 : 1,
        }}
        title={
          fenetrePassee ? 'Livrée depuis trop longtemps : la fenêtre de demande est refermée.'
          : horsOutil ? "« Avis Google » a été coché à la main : la demande s'est faite ailleurs."
          : fige && !avis.envoye ? 'Échéance passée : la demande ne s’annule plus.'
          : undefined
        }
      >
        <input
          type="checkbox"
          // Cochée à la main hors de l'outil : orange, comme les étapes forcées.
          className={horsOutil ? 'manuelle' : undefined}
          checked={avis.demande || horsOutil}
          disabled={fige || occupe}
          onChange={() => void basculer()}
        />
        {etiquette}
        {avis.automatique && (
          <span className="tag" title="Cochée d'office : la commande a été livrée rapidement.">
            auto
          </span>
        )}
      </label>

      {/* Un échec d'appel ne doit pas rester muet : sans cette ligne, une
          demande jamais partie se lit exactement comme une demande partie. */}
      {avis.erreur && !avis.envoye && (
        <div className="sub" style={{ color: 'var(--brick)', margin: '3px 0 0' }}>
          Dernier essai en échec — {avis.erreur} Le passage suivant réessaiera.
        </div>
      )}

      {/* Le numéro manquant ou illisible se voit AVANT le clic, dans les deux
          vues : sans cela, la fenêtre de saisie tomberait sans prévenir. */}
      {fenetrePassee && (
        <div className="sub" style={{ margin: '3px 0 0' }}>Fenêtre refermée</div>
      )}

      {!avis.demande && !fige && aFournir && (
        <div className="sub" style={{ color: 'var(--brick)', margin: '3px 0 0' }}>
          {avis.telephone.brut
            ? <>Numéro inutilisable — <span className="mono">{avis.telephone.brut}</span></>
            : 'Aucun numéro de téléphone'}
        </div>
      )}

      {!compact && !avis.demande && !fige && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '6px 0 0' }}>
          {aFournir
            ? 'La demande ne peut pas partir sans numéro de téléphone : cocher en demandera un.'
            : avis.partiraMaintenant
            ? "La commande est livrée depuis plus longtemps que le délai d'attente : cocher "
              + 'enverra la demande immédiatement, après confirmation.'
            : 'Cocher programme l\'envoi pour trois jours après la livraison, à 10 h, et jamais '
              + 'un week-end ni un jour férié. La case reste offerte deux semaines après la '
              + 'livraison.'}
        </p>
      )}

      {saisieTelephone && (
        <SaisirTelephoneAvis
          commande={commande}
          enAttente={programmer.isPending}
          envoiImmediat={avis.partiraMaintenant}
          onValider={(tel) => void cocher(tel)}
          onFermer={() => setSaisieTelephone(false)}
        />
      )}

      {confirmation && (
        <ConfirmerAvisImmediat
          commande={commande}
          enAttente={programmer.isPending}
          onConfirmer={() => void cocher()}
          onFermer={() => setConfirmation(false)}
        />
      )}
    </div>
  );
}
